import * as React from "react";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Grid,
  Link,
  Stack,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArchiveIcon from "../../../../../../assets/images/archive.svg";
import { useParams } from "react-router-dom";
import { formatToUsDateFromUTCNew, formatTimeWithAMPM } from "../../../../utils/DateHelper";
import NewOutreachModal from "../../../../modals/NewOutreachModal";
import { AuthenticationContext } from "../../../../Context";
import { getHeaders } from "../../../../utils/HeaderHelper";

// --- Types ---
interface OutreachRecord {
  id: number;
  date_time_of_outreach: string;
  contact_method: string;
  category: string;
  provider_id: number;
  provider_name: string | null;
  outcome: string;
  notes: string;
  archived_status: boolean;
  archived_at: string | null;
  archived_by: number | null;
  created_by_user_id: number;
  created_at: string;
  updated_at: string;
}

interface Props {
  patient_id?: string;
}

const headerCellSx = { fontFamily: "QuicksandBold" };

const archivedBadgeSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "10px",
  px: "12px",
  py: 0,
  minWidth: "100px",
  lineHeight: "20px",
  fontSize: "10px",
  fontFamily: "QuicksandMedium",
  fontWeight: 600,
  letterSpacing: "0.4px",
  background: "#FFD9D9",
  color: "#F00",
};

interface NoteProps {
  id: number;
  note: string;
  isExpanded: boolean;
  onToggle: (id: number) => void;
}

const NoteCell: React.FC<NoteProps> = ({ id, note, isExpanded, onToggle }) => {
  if (!note) return <>-</>;
  const maxLength = 60;

  if (note.length <= maxLength) return <>{note}</>;

  return (
    <>
      {isExpanded ? note : `${note.substring(0, maxLength)}... `}
      <Link
        onClick={() => onToggle(id)}
        sx={{
          cursor: "pointer",
          fontSize: "0.85rem",
          ml: 0.5,
          fontWeight: "bold",
          color: "#1976d2",
          textDecorationColor: "#1976d2",
        }}
      >
        {isExpanded ? "Show Less" : "Read More"}
      </Link>
    </>
  );
};

interface OutcomePillProps {
  outcome: string;
}

const OutcomePill: React.FC<OutcomePillProps> = ({ outcome }) => {
  if (!outcome) return <>-</>;

  const isSuccess = outcome.toLowerCase() === "successful";

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "10px",
        px: "12px",
        py: 0,
        minWidth: "100px",
        lineHeight: "20px",
        fontSize: "10px",
        fontFamily: "QuicksandMedium",
        fontWeight: 600,
        letterSpacing: "0.4px",
        background: isSuccess ? "#e8f5e9" : "#FFD9D9",
        color: isSuccess ? "#2e7d32" : "#F00",
      }}
    >
      {outcome}
    </Box>
  );
};

const OutreachHistory: React.FC<Props> = ({ patient_id }) => {
  const authContext = React.useContext<any>(AuthenticationContext);
  let { id: paramPatientId } = useParams();
  const resolvedPatientId = patient_id || paramPatientId || "";
  if (!resolvedPatientId && process.env.NODE_ENV === "development") {
    console.warn("[OutreachHistory] patientId is undefined — outreach will have no patient association.");
  }

  const [openOutreachModal, setOpenOutreachModal] = React.useState(false);
  const [showArchived, setShowArchived] = React.useState(false);
  const [expandedNotes, setExpandedNotes] = React.useState<Record<number, boolean>>({});
  const [outreachData, setOutreachData] = React.useState<OutreachRecord[]>([]);
  const [loading, setLoading] = React.useState(false);

  const fetchOutreachRecords = React.useCallback(() => {
    if (!resolvedPatientId) return;
    setLoading(true);
    fetch(
      `/patients/${resolvedPatientId}/outreach_records?include_archived=${showArchived}`,
      {
        method: "GET",
        headers: getHeaders(authContext?.csrfToken),
      }
    )
      .then((res) => res.json())
      .then((result) => {
        if (result?.success && Array.isArray(result?.resource)) {
          setOutreachData(result.resource);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [resolvedPatientId, showArchived, authContext?.csrfToken]);

  React.useEffect(() => {
    fetchOutreachRecords();
  }, [fetchOutreachRecords]);

  const toggleNoteExpand = React.useCallback((id: number) => {
    setExpandedNotes((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handleArchive = React.useCallback((id: number) => {
    fetch(
      `/patients/${resolvedPatientId}/outreach_records/${id}/archive`,
      {
        method:  "PATCH",
        headers: getHeaders(authContext?.csrfToken),
      }
    )
      .then((res) => res.json())
      .then((result) => {
        if (result?.success) {
          fetchOutreachRecords();
        }
      })
      .catch(() => {});
  }, [resolvedPatientId, authContext?.csrfToken, fetchOutreachRecords]);

  const visibleOutreach = React.useMemo(() => {
    if (showArchived) return outreachData;
    return outreachData.filter((r) => !r.archived_status);
  }, [outreachData, showArchived]);

  return (
    <Grid container className="panel-container">
      <Grid item xs={12}>
        <Grid container className="panel-show-container">
          <Grid container className="panel-information-container">

            {/* Header Section */}
            <Grid container direction="row" className="admin-header">
              <Grid item xs={12} className="box-header">
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  paddingX={3}
                  paddingY={1}
                  alignItems={"center"}
                >
                  <Grid item>
                    <h3>Outreach History</h3>
                  </Grid>
                  <Stack direction="row" alignItems="center" sx={{ gap: "16px" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showArchived}
                          onChange={(e) => setShowArchived(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Show Archived Events"
                      sx={{ ml: 0, color: "text.secondary", "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
                    />
                    <Link
                      className="action-link add-encounter"
                      onClick={() => setOpenOutreachModal(true)}
                    >
                      <AddIcon className="plus-icon" />{" "}
                      <span className="app-user-text" style={{ marginLeft: "0" }}>
                        New Outreach
                      </span>
                    </Link>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>

            {/* Table Section */}
            <Grid container className="grey-container" style={{ marginTop: "-11px" }}>
              <Grid container className="tabs-container">
                <Grid
                  item
                  xs={12}
                  className="panel-body tab-body-container"
                  padding={0}
                  mt={1}
                  sx={{ border: "0px !important" }}
                >
                  <TableContainer>
                    <Table>
                      <TableHead className="table-header-box">
                        <TableRow>
                          <TableCell sx={{ ...headerCellSx, width: "13%" }}>Date</TableCell>
                          <TableCell sx={{ ...headerCellSx, width: "11%" }}>Contact Method</TableCell>
                          <TableCell sx={{ ...headerCellSx, width: "11%" }}>Category</TableCell>
                          <TableCell sx={{ ...headerCellSx, width: "12%" }}>Provider</TableCell>
                          <TableCell sx={{ ...headerCellSx, width: "11%" }}>Outcome</TableCell>
                          <TableCell sx={{ ...headerCellSx, width: "36%" }}>Notes</TableCell>
                          <TableCell align="center" sx={{ ...headerCellSx, width: "6%" }}>
                            Archive
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>

                        {loading && (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                              <CircularProgress size={24} />
                            </TableCell>
                          </TableRow>
                        )}

                        {!loading && visibleOutreach.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 4, color: "#666" }}>
                              {showArchived
                                ? "No outreach history found for this patient."
                                : "No active outreach history found for this patient."}
                            </TableCell>
                          </TableRow>
                        )}

                        {!loading && visibleOutreach.map((outreach) => (
                          <TableRow
                            key={outreach.id}
                            sx={{ "& td, & th": { borderBottom: "none" } }}
                          >
                            <TableCell className="date-data">
                              {formatToUsDateFromUTCNew(outreach.date_time_of_outreach)}{" "}
                              {formatTimeWithAMPM(outreach.date_time_of_outreach)}
                            </TableCell>
                            <TableCell>{outreach.contact_method}</TableCell>
                            <TableCell>{outreach.category}</TableCell>
                            <TableCell>{outreach.provider_name ?? "—"}</TableCell>
                            <TableCell sx={{ pl: 0, pr: 0 }}>
                              <OutcomePill outcome={outreach.outcome} />
                            </TableCell>
                            <TableCell>
                              <NoteCell
                                id={outreach.id}
                                note={outreach.notes}
                                isExpanded={!!expandedNotes[outreach.id]}
                                onToggle={toggleNoteExpand}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {outreach.archived_status ? (
                                <Box sx={archivedBadgeSx}>Archived</Box>
                              ) : (
                                <Tooltip title="Archive Record" arrow>
                                  <IconButton
                                    onClick={() => handleArchive(outreach.id)}
                                    size="small"
                                  >
                                    <img src={ArchiveIcon} alt="Archive" style={{ width: 26, height: 26 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}

                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Grid>

          </Grid>
        </Grid>
      </Grid>

      <NewOutreachModal
        open={openOutreachModal}
        onClose={() => setOpenOutreachModal(false)}
        patientId={resolvedPatientId}
        onSaveSuccess={() => {
          setOpenOutreachModal(false);
          fetchOutreachRecords();
        }}
      />

    </Grid>
  );
};

export default OutreachHistory;
